import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faSearch, faUser, faSignOutAlt, faHome,
  faHistory, faCog, faMoneyBillWave, faUsers, faClock,
  faBell, faWarehouse
} from '@fortawesome/free-solid-svg-icons';
import { FaFolderOpen, FaCalendarAlt, FaUserTie, FaMapMarkedAlt } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import pb from '../api/pocketbase';

// Helper function to safely extract rubrique string
const getRubriqueString = (rubriqueData) => {
  if (!rubriqueData) return '';
  if (typeof rubriqueData === 'string') return rubriqueData;
  if (typeof rubriqueData === 'object' && rubriqueData.rubrique) {
    return rubriqueData.rubrique;
  }
  return '';
};

// Helper function to safely extract sous-rubrique string
const getSousRubriqueString = (rubriqueData) => {
  if (!rubriqueData) return '';
  if (typeof rubriqueData === 'object' && rubriqueData.sousRubrique) {
    return rubriqueData.sousRubrique;
  }
  return '';
};

const ImmobilisationsPage = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const [montant, setMontant] = useState('');
  const [rubrique, setRubrique] = useState('');
  const [sousRubrique, setSousRubrique] = useState('');
  const [description, setDescription] = useState('');
  const [immobilisations, setImmobilisations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeNotifTab, setActiveNotifTab] = useState('validées');
  const [notifications, setNotifications] = useState({
    validees: [],
    attentes: []
  });
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);

  // État pour la modale de déconnexion
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Fonction de déconnexion améliorée
  const handleLogout = () => {
    // Clear authentication
    pb.authStore.clear();
    
    // Clear all local storage and session storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Rediriger vers la page de connexion
    window.location.replace('/login');
    
    // Empêcher la navigation arrière
    window.history.pushState(null, null, '/login');
    window.onpopstate = function () {
      window.history.pushState(null, null, '/login');
    };
  };

  // Empêcher la navigation arrière après déconnexion
  useEffect(() => {
    const handlePopState = (event) => {
      if (!pb.authStore.isValid) {
        event.preventDefault();
        window.history.pushState(null, null, '/login');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Structure des rubriques et sous-rubriques des immobilisations
  const immobilisationsRubriques = {
    'TERRAINS': [
      'TERRAINS NUS',
      'TERRAINS BATIS',
      'TERRAINS AMENAGES',
      'AUTRES TERRAINS'
    ],
    'BÂTIMENTS, INSTALLATIONS TECHNIQUES ET AGENCEMENTS': [
      'BÂTIMENTS INDUSTRIELS, AGRICOLES, ADMINISTRATIFS ET COMMERCIAUX SUR SOL PROPRE',
      'BÂTIMENTS INDUSTRIELS, AGRICOLES, ADMINISTRATIFS ET COMMERCIAUX SUR SOL D\'AUTRUI',
      'AMENANGEMENTS, AGENCEMENTS ET INSTALLATIONS TECHNIQUES',
      'AMENAGEMENTS DE BUREAUX',
      'AUTRES INSTALLATIONS ET AGENCEMENTS',
      'BATIMENTS AMENAGEMENTS ET INTALLATIONS EN COURS'
    ],
    'MATÉRIEL, MOBILIER ET ACTIFS BIOLOGIQUES': [
      'MATÉRIEL ET OUTILLAGE INDUSTRIEL ET COMMERCIAL',
      'MATÉRIEL ET MOBILIER',
      'MATÉRIEL DE TRANSPORT'
    ],
    'AVANCES ET ACOMPTES VERSÉS SUR IMMOBILISATIONS': [
      'AVANCES ET ACOMPTES VERSÉS SUR IMMOBILISATIONS INCORPORELLES',
      'AVANCES ET ACOMPTES VERSÉS SUR IMMOBILISATIONS CORPORELLES'
    ]
  };

  // Charger les immobilisations existantes depuis PocketBase au chargement du composant
  const [viewedImmobilisationUpdates, setViewedImmobilisationUpdates] = useState(() =>
    JSON.parse(localStorage.getItem("viewedImmobilisationUpdates_CG") || "[]")
  );

  // Fonction pour marquer une immobilisation modifiée comme vue
  const markImmobilisationUpdateAsViewed = (immobilisationId) => {
    const updateKey = `immobilisationMEA-${immobilisationId}`;
    if (!viewedImmobilisationUpdates.includes(updateKey)) {
      const newViewed = [...viewedImmobilisationUpdates, updateKey];
      setViewedImmobilisationUpdates(newViewed);
      localStorage.setItem("viewedImmobilisationUpdates_CG", JSON.stringify(newViewed));
      pb.collection('immobilisationmea').update(immobilisationId, {
        hasNewUpdate: false,
        viewedByCG: true,
        dateViewedByCG: new Date().toISOString()
      }).catch(error => {
        console.error("Erreur lors du marquage de l'immobilisation comme vue:", error);
      });
    }
  };

  // Wrapper safe pour unsubscribe (ignore auth mismatch 403)
  const safeUnsubscribe = async (collectionName) => {
    try {
      await pb.collection(collectionName).unsubscribe();
    } catch (error) {
      if (error?.status === 403) {
        console.debug(`Auth mismatch on unsubscribe ${collectionName}, ignoring`);
      } else {
        console.error(`Error unsubscribing from ${collectionName}:`, error);
      }
    }
  };

  useEffect(() => {
    const fetchImmobilisations = async () => {
      setLoading(true);
      try {
        // Toujours récupérer les immobilisations validées par le DG pour l'historique
        const records = await pb.collection('immoValideesDG').getFullList({ 
          sort: '-created',
          requestKey: 'fetchImmoHistory'
        });
        setImmobilisations(records);
      } catch (error) {
        console.error('Erreur de chargement:', error);
      } finally {
        setLoading(false);
      }
    };

    const subscribeToImmobilisations = async () => {
      if (!pb.authStore.isValid) return;
      
      try {
        // S'abonner aux changements en temps réel sur immoValideesDG
        await pb.collection('immoValideesDG').subscribe('*', function (e) {
          // Quand une nouvelle immobilisation validée arrive, refetch
          pb.collection('immoValideesDG').getFullList({ 
            sort: '-created',
            requestKey: 'fetchImmoHistoryRT'
          }).then(records => {
            setImmobilisations(records);
          }).catch(error => {
            console.debug('Erreur lors de la mise à jour temps réel:', error?.message);
          });
        });
      } catch (error) {
        console.debug('Subscription immobilisations skipped:', error?.message);
      }
    };

    fetchImmobilisations();

    return () => {};
  }, [activeView]);

  // Charger les notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const hiddenIds = JSON.parse(localStorage.getItem('hiddenNotificationsImmobilisations') || '[]');
        
        // Récupérer les notifications validées
        const valides = await pb.collection('immoValideesDG').getFullList({
          sort: '-created'
        });
        
        // Récupérer les notifications en attente pour le CG.
        // Celles-ci ont le statut 'en attente' (ou un statut vide/nul par défaut).
        // On exclut 'modifiée' car le CG vient de la traiter.
        let attentes = [];
        try {
          attentes = await pb.collection('immomea').getFullList({
            filter: 'statut = "en attente" || statut = "" || statut = null',
            sort: '-created',
          });
        } catch (error) {
          console.error('Erreur lors de la récupération des notifications en attente:', error);
          // En cas d'erreur, on retourne un tableau vide pour ne pas planter.
          attentes = [];
        }

        
        console.log('Total notifications en attente trouvées:', attentes.length);
        console.log('Notifications en attente:', attentes);

        setNotifications({
          validees: valides.filter(n => !hiddenIds.includes(n.id)).map(n => ({ ...n, type: 'valideeDG' })),
          attentes: attentes
            .filter(n => !hiddenIds.includes(n.id))
            .map(n => ({ ...n, type: 'attenteCG' }))
            .sort((a, b) => (b.hasNewUpdate ? 1 : 0) - (a.hasNewUpdate ? 1 : 0))
        });
      } catch (error) {
        console.error('Erreur chargement notifications:', error);
      }
    };
    fetchNotifications();

    let unsubValidees;
    let unsubAttentes;

    if (pb.authStore.isValid) {
      try {
        pb.collection('immoValideesDG').subscribe('*', fetchNotifications)
          .then(unsub => { unsubValidees = unsub; })
          .catch(err => console.debug("Sub immoValideesDG skipped:", err?.message));

        pb.collection('immomea').subscribe('*', fetchNotifications)
          .then(unsub => { unsubAttentes = unsub; })
          .catch(err => console.debug("Sub immomea skipped:", err?.message));
      } catch (err) {
        console.debug("Subscriptions skipped:", err?.message);
      }
    }

    return () => {
      if (unsubValidees) unsubValidees();
      if (unsubAttentes) unsubAttentes();
    };
  }, []);

  // Formatage des dates
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      dateStyle: 'long',
      timeStyle: 'short'
    });
  };

  // État pour l'édition
  const [editImmobilisation, setEditImmobilisation] = useState(null);

  // Modifie le formulaire pour supporter l'édition
  useEffect(() => {
    if (editImmobilisation) {
      setMontant(editImmobilisation.montant);
      setRubrique(getRubriqueString(editImmobilisation.rubrique));
      setSousRubrique(getSousRubriqueString(editImmobilisation.rubrique) || editImmobilisation.sousRubrique || '');
      setDescription(editImmobilisation.description);
      setActiveView('dashboard');
      setShowNotifications(false);
    }
  }, [editImmobilisation]);

  // Gérer le changement de rubrique
  const handleRubriqueChange = (e) => {
    const selectedRubrique = e.target.value;
    setRubrique(selectedRubrique);
    // Réinitialiser la sous-rubrique lors du changement de rubrique
    setSousRubrique('');
  };

  

const handleEditImmobilisation = (notif) => {
  // Préparer le formulaire pour l'édition.
  // Le `useEffect` dépendant de `editImmobilisation` s'occupera de:
  // - Remplir le formulaire avec les données de `notif`
  // - Changer la vue pour 'dashboard'
  // - Fermer le panneau de notifications
  setEditImmobilisation(notif);
};




  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!montant || !rubrique || !sousRubrique || !description) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      setLoading(true);
      if (editImmobilisation) {
        // 1. Mettre à jour la notification 'immomea' pour la renvoyer
        await pb.collection('immomea').update(editImmobilisation.id, {
          montant: Number(montant),
          rubrique: {
            rubrique: rubrique,
            sousRubrique: sousRubrique
          },
          description, // Utilise la nouvelle description du formulaire
          statut: 'modifiée',
          hasNewUpdate: true,
          updateCount: (editImmobilisation.updateCount || 0) + 1,
          dateModification: new Date().toISOString(),
          viewedByCG: false,
          meaPar: "Controleur de Gestion",
        });

        // 2. Créer une nouvelle immobilisation avec les données à jour
        const newImmobilisation = {
          montant: Number(montant),
          rubrique,
          sousRubrique,
          description,
          dateCreation: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          statut: 'en attente',
        };
        await pb.collection('immobilisation').create(newImmobilisation);

        // 3. Créer/Mettre à jour l'enregistrement depensemea correspondant
        const depenseMEAData = {
          montant: Number(montant),
          rubrique: rubrique,
          sousRubrique: sousRubrique,
          description: description,
          datemea: new Date().toISOString(),
          status: 'en_attente_CG',
          meaPar: 'Controleur de Gestion',
          hasNewUpdate: true,
          updateCount: (editImmobilisation.updateCount || 0) + 1,
          dateModification: new Date().toISOString(),
        };
        try {
          await pb.collection('depensemea').getOne(editImmobilisation.id);
          await pb.collection('depensemea').update(editImmobilisation.id, depenseMEAData);
        } catch {
          await pb.collection('depensemea').create(depenseMEAData);
        }

        // 4. Masquer la notification de la liste locale
        const hiddenIds = JSON.parse(localStorage.getItem('hiddenNotificationsImmobilisations') || '[]');
        if (!hiddenIds.includes(editImmobilisation.id)) {
          hiddenIds.push(editImmobilisation.id);
          localStorage.setItem('hiddenNotificationsImmobilisations', JSON.stringify(hiddenIds));
        }
        alert('Immobilisation modifiée et renvoyée avec succès');
        // Retirer la notification de la liste d'attente
        setNotifications(prev => ({
          ...prev,
          attentes: prev.attentes.filter(n => n.id !== editImmobilisation.id)
        }));
        setEditImmobilisation(null);
        setShowNotifications(false); // Optionnel : ferme la modale après action
      } else {

        // Création normale - SEULEMENT dans immobilisation
        const newImmobilisation = {
          montant: Number(montant),
          rubrique,
          sousRubrique,
          description,
          dateCreation: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          statut: 'en attente',
        };
        await pb.collection('immobilisation').create(newImmobilisation);
        alert('Immobilisation enregistrée avec succès');
      }
      // Réinitialisation du formulaire
      setMontant('');
      setRubrique('');
      setSousRubrique('');
      setDescription('');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'opération');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (immobilisationId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette immobilisation ?')) {
      setImmobilisations(prevImmobilisations => prevImmobilisations.filter(immobilisation => immobilisation.id !== immobilisationId));
      alert('Immobilisation supprimée avec succès!');
    }
  };

  // Fonction pour masquer une notification (persistant)
  const hideNotification = (notif, type) => {
    const hiddenIds = JSON.parse(localStorage.getItem('hiddenNotificationsImmobilisations') || '[]');
    if (!hiddenIds.includes(notif.id)) {
      hiddenIds.push(notif.id);
      localStorage.setItem('hiddenNotificationsImmobilisations', JSON.stringify(hiddenIds));
    }
    if (type === "validées") {
      setNotifications(prev => ({
        ...prev,
        validees: prev.validees.filter(n => n.id !== notif.id)
      }));
    } else {
      setNotifications(prev => ({
        ...prev,
        attentes: prev.attentes.filter(n => n.id !== notif.id)
      }));
    }
  };

  // Calculer les totaux par rubrique
  const totauxParRubrique = immobilisations.reduce((acc, immobilisation) => {
    const rubriqueStr = getRubriqueString(immobilisation.rubrique);
    acc[rubriqueStr] = (acc[rubriqueStr] || 0) + Number(immobilisation.montant);
    return acc;
  }, {});

  const newAttenteCount = notifications.attentes.filter(n => n.hasNewUpdate).length;

  // Composant pour afficher les notifications
  const NotificationsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Notifications des immobilisations</h3>
          <button
            onClick={() => setShowNotifications(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveNotifTab("validées")}
              className={`px-4 py-2 rounded flex items-center gap-2 ${
                activeNotifTab === "validées"
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Immobilisations validées
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                activeNotifTab === "validées"
                  ? "bg-white text-green-600"
                  : "bg-green-200 text-green-700"
              }`}>
                {notifications.validees.length}
              </span>
            </button>
            <button
              onClick={() => setActiveNotifTab("attentes")}
              className={`px-4 py-2 rounded flex items-center gap-2 ${
                activeNotifTab === "attentes"
                  ? "bg-yellow-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              Mises en attente
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                activeNotifTab === "attentes"
                  ? "bg-white text-yellow-600"
                  : "bg-yellow-200 text-yellow-700"
              }`}>
                {notifications.attentes.length}
              </span>
            </button>
          </div>
          {activeNotifTab === "validées" ? (
            notifications.validees.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <span className="text-gray-400 text-4xl mb-3">🔔</span>
                <p className="text-gray-600">Aucune notification de validation</p>
              </div>
            ) : (
              // Pour les notifications validées :
            notifications.validees.map((notif) => (
              <div
                key={notif.id}
                className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500 animate-fade-in flex justify-between items-start mb-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-500">✓</span>
                    <span className="font-semibold">
                      Validé par Directeur Général
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Date : {notif.dateValidationDG?.toDate
                      ? notif.dateValidationDG.toDate().toLocaleString()
                      : notif.dateValidationDG
                        ? new Date(notif.dateValidationDG).toLocaleString()
                        : ""}
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="font-medium">
                      {getRubriqueString(notif.rubrique)}
                    </div>
                    {getSousRubriqueString(notif.rubrique) && (
                      <div className="text-sm text-blue-600 font-medium">
                        → {getSousRubriqueString(notif.rubrique)}
                      </div>
                    )}
                    <div className="text-sm text-gray-600">
                      Montant : {notif.montant?.toLocaleString()} FCFA
                    </div>
                    {notif.description && (
                      <div className="mt-1 text-sm text-gray-700">
                        Description : {notif.description}
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <button
                      className="text-red-600 hover:underline text-sm"
                      onClick={() => hideNotification(notif, "validées")}
                    >
                      Supprimer la notification
                    </button>
                  </div>
                </div>
              </div>
            ))
            )
          ) : (
            notifications.attentes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <span className="text-gray-400 text-4xl mb-3">🔔</span>
                <p className="text-gray-600">Aucune immobilisation en attente</p>
              </div>
            ) : (
              notifications.attentes.map((notif) => (
              <div
                key={notif.id}
                className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500 animate-fade-in flex justify-between items-start mb-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-yellow-500">⏳</span>
                    <span className="font-semibold text-yellow-700">
                      Mise en attente par Contrôleur de Gestion
                    </span>
                    {notif.hasNewUpdate && (
                      <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                        NOUVEAU
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Date : {notif.datemea
                      ? new Date(notif.datemea).toLocaleString()
                      : ""}
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="font-medium">
                      {getRubriqueString(notif.rubrique)}
                    </div>
                    {getSousRubriqueString(notif.rubrique) && (
                      <div className="text-sm text-blue-600 font-medium">
                        → {getSousRubriqueString(notif.rubrique)}
                      </div>
                    )}
                    <div className="text-sm text-gray-600">
                      Montant : {notif.montant?.toLocaleString()} FCFA
                    </div>
                    {notif.description && (
                      <div className="mt-1 text-sm text-gray-700">
                        Description : {notif.description}
                      </div>
                    )}
                    {notif.motif && (
                      <div className="mt-1 text-sm text-red-600 font-semibold">
                        Motif : {notif.motif}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex gap-3">
                    <button
                      className="text-blue-600 hover:underline text-sm"
                      onClick={() => handleEditImmobilisation(notif)}
                    >
                      Modifier et renvoyer
                    </button>
                  </div>
                </div>
              </div>
            ))
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-200 text-gray-900 min-h-screen flex flex-col animate-fade-in">
      {/* HEADER */}
      <div className="fixed top-0 left-0 right-0 z-50 flex bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 h-14 items-center justify-between">
        <div className="flex items-center space-x-4 min-w-0">
          <img src="dywanes-logo.png" alt="Dywane Logo" className="h-10 w-auto" />
        </div>
        <div className="flex-1 max-w-lg mx-4">
          <div className="relative text-gray-400 focus-within:text-gray-600">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input type="search" placeholder="Rechercher..." className="block w-full rounded-md border border-gray-300 bg-gray-100 py-1.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-gray-400 focus:ring-0 focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center space-x-2 min-w-0">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-600 text-sm select-none">
            <FontAwesomeIcon icon={faUser} />
          </div>
<span className="text-sm truncate min-w-0">{currentUser?.email || 'immobilisations'}</span>
          <button 
            onClick={() => setShowLogoutModal(true)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>

        </div>
      </div>

      {/* BODY */}
      <div className="flex flex-1 overflow-hidden">
        {/* SIDEBAR */}
        <nav className="fixed top-14 left-0 z-40 h-[calc(100vh-3.5rem)] bg-gray-900 w-56 flex-shrink-0 px-4 py-6 flex flex-col space-y-6 text-gray-300 text-sm select-none animate-slide-in-left">
          <button 
            onClick={() => setActiveView('dashboard')}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 transition-colors ${
              activeView === 'dashboard' ? 'bg-blue-800 text-white' : 'hover:bg-gray-800'
            }`}
          >
            <FontAwesomeIcon icon={faHome} /><span>Tableau de bord</span>
          </button>

          <button 
            onClick={() => setActiveView('history')}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 transition-colors ${
              activeView === 'history' ? 'bg-blue-800 text-white' : 'hover:bg-gray-800'
            }`}
          >
            <FontAwesomeIcon icon={faHistory} /><span>Historique</span>
          </button>

          <button
            onClick={() => setShowNotifications(true)}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 transition-colors hover:bg-gray-800 relative`}
          >
            <FontAwesomeIcon icon={faBell} />
            <span>Notifications</span>
            {newAttenteCount > 0 && (
              <span className="absolute top-1 right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                {newAttenteCount}
              </span>
            )}
          </button>

          <div className="flex-grow" />
          <div className="border-t border-gray-700 pt-3">
            <a href="#" className="flex items-center space-x-2 hover:text-white">
              <FontAwesomeIcon icon={faCog} /><span>Paramètres</span>
            </a>
            <button 
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center space-x-2 mt-4 hover:text-white w-full text-left"
            >
              <FontAwesomeIcon icon={faSignOutAlt} /><span>Déconnexion</span>
            </button>

          </div>
        </nav>

        {/* MAIN CONTENT */}
        <main className="flex-1 overflow-auto p-6 pl-60 pt-20 animate-slide-in-right">
          <div className="space-y-6">
            {/* Header commun */}
            <div className="bg-white shadow mb-4">
              <div className="px-4 sm:px-6 lg:px-8 py-3">
                <div className="flex justify-between items-center">
                  <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faWarehouse} className="mr-3 text-blue-600" />
                    {activeView === 'dashboard' ? 'Gestion des Immobilisations' : 'Historique des Immobilisations'}
                  </h1>
                </div>
              </div>
            </div>

            {/* Contenu conditionnel */}
            {activeView === 'dashboard' ? (
              // Vue Tableau de bord
              <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-4">
                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Total des Immobilisations</p>
                        <p className="text-2xl font-bold mt-2">
                          {immobilisations.reduce((sum, immobilisation) => sum + Number(immobilisation.montant), 0).toLocaleString()} FCFA
                        </p>
                      </div>
                      <FontAwesomeIcon icon={faMoneyBillWave} className="text-3xl opacity-75" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Nombre d'Immobilisations</p>
                        <p className="text-2xl font-bold mt-2">{immobilisations.length}</p>
                      </div>
                      <FontAwesomeIcon icon={faWarehouse} className="text-3xl opacity-75" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Dernière mise à jour</p>
                        <p className="text-2xl font-bold mt-2">
                          {immobilisations[0]?.dateCreation
                            ? new Date(immobilisations[0].dateCreation).toLocaleDateString('fr-FR')
                            : 'N/A'}
                        </p>
                      </div>
                      <FontAwesomeIcon icon={faClock} className="text-3xl opacity-75" />
                    </div>
                  </div>
                </div>

                {/* Formulaire */}
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {editImmobilisation ? 'Modifier l\'Immobilisation' : 'Nouvelle Immobilisation'}
                    </h2>
                  </div>
                  <div className="p-6">
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Montant</label>
                        <input
                          type="number"
                          value={montant}
                          onChange={(e) => setMontant(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Entrez le montant en FCFA"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rubrique</label>
                        <select
                          value={rubrique}
                          onChange={handleRubriqueChange}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Sélectionnez une rubrique</option>
                          {Object.keys(immobilisationsRubriques).map((rub) => (
                            <option key={rub} value={rub}>{rub}</option>
                          ))}
                        </select>
                      </div>

                      {/* Dropdown pour les sous-rubriques */}
                      {rubrique && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sous-catégorie *
                          </label>
                          <select
                            value={sousRubrique}
                            onChange={(e) => setSousRubrique(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            <option value="">Sélectionnez une sous-catégorie</option>
                            {immobilisationsRubriques[rubrique]?.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          rows="3"
                          placeholder="Décrivez l'immobilisation..."
                        />
                      </div>

                      <div className="md:col-span-2 flex justify-end gap-3">
                        {editImmobilisation && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditImmobilisation(null);
                              setMontant('');
                              setRubrique('');
                              setSousRubrique('');
                              setDescription('');
                            }}
                            className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors duration-200"
                          >
                            Annuler
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className={`bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {loading 
                            ? 'Enregistrement...' 
                            : editImmobilisation 
                              ? 'Modifier et renvoyer' 
                              : 'Enregistrer l\'immobilier'
                          }
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            ) : (
              // Vue Historique
              <div className="px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-600" />
                        Historique des Immobilisations
                      </h2>
                      <div className="flex gap-4">
                        <span className="text-sm text-gray-500">
                          Total: {immobilisations.length} immobilisation(s)
                        </span>
                        <span className="text-sm font-semibold text-gray-700">
                          Montant Total: {immobilisations.reduce((sum, immo) => sum + (Number(immo.montant) || 0), 0).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Rubrique
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Description
                          </th>
                          <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Montant
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {immobilisations.map((immobilisation, index) => (
                          <tr key={immobilisation.id} className={`
                            transition-colors hover:bg-blue-50
                            ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          `}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {immobilisation.created
                                ? new Date(immobilisation.created).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })
                                : 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {getRubriqueString(immobilisation.rubrique)}
                                </span>
                                {getSousRubriqueString(immobilisation.rubrique) && (
                                  <div className="text-xs text-blue-600 mt-1 font-medium">
                                    → {getSousRubriqueString(immobilisation.rubrique)}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 line-clamp-2">
                                {immobilisation.description}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="text-sm font-semibold text-gray-900">
                                <span className="font-mono">{Number(immobilisation.montant).toLocaleString()} FCFA</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {immobilisations.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <FontAwesomeIcon icon={faWarehouse} className="text-4xl mb-3 opacity-50" />
                      <p>Aucune immobilisation enregistrée pour le moment</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal des notifications */}
      {showNotifications && <NotificationsModal />}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Confirmation de déconnexion</h3>
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-700 text-center">
                Êtes-vous sûr de vouloir vous déconnecter ?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Oui, se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}

export default ImmobilisationsPage;
